import type {OverrideOrDefaultData, CompRef, Pointer} from '@wix/document-services-types'
import type {
    ManifestExtensionAPI,
    InnerElementsExtensionAPI,
    SlotsExtensionAPI,
    StructureExtensionAPI,
    SchemaExtensionAPI
} from '@wix/document-manager-extensions'
import type {CreateExtArgs} from '@wix/document-manager-core'
import {getUpdatedDataItemAtNestedPath, getDataToUpdate} from './slotPopulationUtils'

const removeSlot = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    slotKeyName: string,
    innerElementPath?: string[]
): void => {
    const {extensionAPI} = createExtArgs
    const {innerElements} = extensionAPI as InnerElementsExtensionAPI
    const {slots} = extensionAPI as SlotsExtensionAPI

    if (innerElementPath) {
        innerElements.removeInnerElementSlotData(componentRef as CompRef, innerElementPath, slotKeyName)
    } else {
        slots.removeSlotData(componentRef as CompRef, slotKeyName, false)
    }
}

const processRemovedItems = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    updatedDataItem: any,
    optionalContainerToRemove: OverrideOrDefaultData[]
) => {
    const removedItems = optionalContainerToRemove.filter(({prop, nestedDataPath}) => {
        const currentLevel = getUpdatedDataItemAtNestedPath(nestedDataPath ?? [], updatedDataItem)
        const currentValue = currentLevel?.[prop]
        const isPathMissing = !!nestedDataPath?.length && !currentLevel
        const isItemExists = !!currentLevel?._id
        const isContainerCleared =
            // eslint-disable-next-line lodash/prefer-is-nil
            isItemExists && (currentValue === null || currentValue === undefined || currentValue === '')

        return isPathMissing || isContainerCleared
    })
    removedItems.forEach(({path, value}) => {
        const innerElementPath = path
        const slotKeyName = value
        removeSlot(createExtArgs, componentRef, slotKeyName, innerElementPath)
    })
}

const validateNoDuplicateIds = (data: any) => {
    const seenIds = new Set<string>()
    const duplicateIds = new Set<string>()

    const collectIds = (items: any[]) => {
        items.forEach(item => {
            if (item._id) {
                if (seenIds.has(item._id)) {
                    duplicateIds.add(item._id)
                } else {
                    seenIds.add(item._id)
                }
            }
        })
    }

    Object.values(data).forEach(value => {
        if (Array.isArray(value)) {
            collectIds(value)
        }
    })

    if (duplicateIds.size > 0) {
        const duplicateIdsList = Array.from(duplicateIds).join(', ')
        throw new Error(`Duplicate id values found in update data: ${duplicateIdsList}`)
    }
}

export const builderComponentsRemoveSlotHook = (
    createExtArgs: CreateExtArgs,
    componentRef: Pointer,
    updatedDataItem: any
) => {
    const {extensionAPI} = createExtArgs
    const {manifest} = extensionAPI as ManifestExtensionAPI
    const {schemaAPI} = extensionAPI as SchemaExtensionAPI
    const {components} = extensionAPI as StructureExtensionAPI

    const componentType = components.getComponentType(componentRef)

    if (!schemaAPI.isSchemaCollectionRegistered(componentType)) {
        return
    }

    validateNoDuplicateIds(updatedDataItem)

    const containersListIncludeInnerElement = getDataToUpdate(manifest, componentRef, true)
    const optionalContainerToRemove = containersListIncludeInnerElement.filter(({value}) => !!value)

    processRemovedItems(createExtArgs, componentRef as CompRef, updatedDataItem, optionalContainerToRemove)
}
